import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import {
    getUser as getLocalUser,
    setUser as setLocalUser,
    removeUser as removeLocalUser,
    USER_ROLES
} from '../utils/authHelpers';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUserState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [checked, setChecked] = useState(false);

    // Проверяем аутентификацию при загрузке
    const checkAuth = async () => {
        try {
            setLoading(true);
            const remoteAuth = await authAPI.checkAuth();

            if (remoteAuth.data.is_authenticated) {
                setUserState(remoteAuth.data.user);
                setLocalUser(remoteAuth.data.user);
            } else {
                // Если access-токен истек, бекенд возвращает 200 { is_authenticated: false }
                // Проверим, был ли у нас пользователь в localStorage, если да - попытаемся обновить токен
                const localUser = getLocalUser();
                if (localUser) {
                    try {
                        await authAPI.refreshToken();
                        // Если токен обновился успешно, проверяем еще раз
                        const retryAuth = await authAPI.checkAuth();
                        if (retryAuth.data.is_authenticated) {
                            setUserState(retryAuth.data.user);
                            setLocalUser(retryAuth.data.user);
                            return;
                        }
                    } catch (refreshErr) {
                        console.error('Failed to implicitly refresh token on startup:', refreshErr);
                    }
                }

                setUserState(null);
                removeLocalUser();
            }
        } catch (error) {
            console.error('Auth check failed:', error);
            setUserState(null);
            removeLocalUser();
        } finally {
            setLoading(false);
            setChecked(true);
        }
    };

    // Вход
    const login = async (credentials) => {
        try {
            const response = await authAPI.login(credentials);

            // Теперь токены приходят в HttpOnly Cookies, в ответе только данные пользователя
            const { user: userData } = response.data;

            if (!userData) {
                throw new Error('Данные пользователя не получены');
            }

            setUserState(userData);
            setLocalUser(userData);

            return {
                success: true,
                user: userData
            };
        } catch (error) {
            setUserState(null);
            removeLocalUser();

            let errorMessage = 'Ошибка при входе';

            if (error.response?.status === 429) {
                errorMessage = 'Слишком много попыток входа. Пожалуйста, подождите минуту и попробуйте снова.';
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            } else if (error.response?.status === 401 || error.response?.status === 400) {
                errorMessage = 'Неверный логин или пароль. Пожалуйста, проверьте правильность введенных данных.';
            } else {
                errorMessage = error.message || 'Ошибка при входе';
            }

            return {
                success: false,
                error: errorMessage,
                is_blocked: !!error.response?.data?.is_blocked,
                has_pending_unblock_request: !!error.response?.data?.has_pending_unblock_request
            };
        } finally {
            setLoading(false);
        }
    };

    // Выход
    const logout = async () => {
        try {
            setLoading(true);
            // Отправляем запрос на сервер для инвалидации токена
            await authAPI.logout();
        } catch (error) {
            console.error('Logout API error:', error);
            // Все равно очищаем локальные данные даже при ошибке API
        } finally {
            setUserState(null);
            removeLocalUser();
            setLoading(false);
        }
    };

    // Регистрация
    const register = async (userData) => {
        try {
            setLoading(true);
            const response = await authAPI.register(userData);

            // Если требуется подтверждение email, не пытаемся войти (иначе получим 400 Inactive Account)
            if (response.data && response.data.requires_email_verification) {
                return {
                    success: true,
                    requires_email_verification: true,
                    message: response.data.message
                };
            }

            // После регистрации пробуем войти, используем правильное поле username_or_email
            return await login({
                username_or_email: userData.username,
                password: userData.password
            });
        } catch (error) {
            let errorMessage = 'Ошибка при создании аккаунта.';

            if (error.response?.status === 400 && error.response.data) {
                // Возвращаем данные ошибки (например, {email: [...], username: [...]})
                return {
                    success: false,
                    error: error.response.data
                };
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            } else if (error.message && !error.message.includes('status code')) {
                errorMessage = error.message;
            }

            return {
                success: false,
                error: errorMessage
            };
        } finally {
            setLoading(false);
        }
    };

    // Подтверждение почты кодом
    const verifyEmailCode = async (email, code, password) => {
        try {
            setLoading(true);
            const response = await authAPI.verifyEmailCode(email, code);

            // Если мы передали password (он был сохранен в стейте формы), то можно сразу залогинить
            if (response.data?.message && password) {
                return await login({
                    username_or_email: email,
                    password: password
                });
            }

            return {
                success: true,
                message: response.data?.message || 'Email успешно подтвержден'
            };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.error || 'Неверный код или срок его действия истек.'
            };
        } finally {
            setLoading(false);
        }
    };

    // Обновление данных пользователя
    const updateUser = async (userData) => {
        try {
            const updatedUser = { ...user, ...userData };

            // Гарантируем сохранение роли
            if (user?.role) updatedUser.role = user.role;
            if (userData?.role) updatedUser.role = userData.role;

            setUserState(updatedUser);
            setLocalUser(updatedUser);

            return {
                success: true,
                user: updatedUser
            };
        } catch (error) {
            const errorMessage = error.message || 'Ошибка при обновлении профиля';

            return {
                success: false,
                error: errorMessage
            };
        }
    };

    // Принудительное обновление данных пользователя (например, при смене роли)
    const refreshUser = async () => {
        try {
            const response = await authAPI.checkAuth();
            if (response.data.is_authenticated) {
                const userData = response.data.user;
                // Объединяем старые данные (токены) с новыми данными профиля
                const updatedUser = { ...user, ...userData };

                // Если в userData есть роль, гарантируем её обновление
                if (userData.role) updatedUser.role = userData.role;

                setUserState(updatedUser);
                setLocalUser(updatedUser);
                return { success: true, user: updatedUser };
            }
            return { success: false, error: 'User not authenticated' };
        } catch (error) {
            console.error('Refresh user failed:', error);
            return { success: false, error: error.message };
        }
    };

    // Проверка ролей и разрешений
    const isAdmin = () => {
        if (!user) return false;
        return Boolean(
            user.role === USER_ROLES.ADMIN ||
            user.role === USER_ROLES.ROOT ||
            user.role === 'admin' ||
            user.role === 'root' ||
            user.user_type === 'admin' ||
            user.user_type === 'root' ||
            user.is_admin ||
            user.is_root ||
            user.is_superuser ||
            user.is_staff
        );
    };


    const isStudent = () => {
        return user?.role === USER_ROLES.STUDENT;
    };

    const isMentor = () => {
        return user?.role === USER_ROLES.MENTOR;
    };

    const isTeacher = () => {
        return isMentor();
    };

    const isOrganization = () => {
        return user?.role === USER_ROLES.ORGANIZATION;
    };

    const getUserRole = () => {
        return user?.role || USER_ROLES.USER;
    };

    const hasRole = (role) => {
        if (!user) return false;

        // Админ имеет все роли (admin или root)
        if (isAdmin()) return true;

        if (Array.isArray(role)) {
            return role.includes(user.role);
        }

        return user.role === role;
    };

    const hasPermission = (permission) => {
        if (!user) return false;

        // Админ имеет все разрешения
        if (user.role === USER_ROLES.ADMIN) return true;

        // Проверяем permissions из данных пользователя
        if (user.permissions && Array.isArray(user.permissions)) {
            return user.permissions.includes(permission);
        }

        // Базовые проверки по роли
        const rolePermissions = {
            [USER_ROLES.MENTOR]: ['manage_content', 'manage_users', 'moderate'],
            [USER_ROLES.ORGANIZATION]: ['create_projects', 'manage_team', 'invite_users'],
            [USER_ROLES.STUDENT]: ['join_projects', 'submit_work', 'comment'],
            [USER_ROLES.USER]: ['view_content', 'like', 'save']
        };

        const permissionsForRole = rolePermissions[user.role] || [];
        return permissionsForRole.includes(permission);
    };

    // Обновление токена (при истечении срока)
    const refreshToken = async () => {
        try {
            await authAPI.refreshToken();
            return { success: true };
        } catch (error) {
            // Если не удалось обновить токен, разлогиниваем
            logout();
            return { success: false, error: 'Сессия истекла' };
        }
    };

    useEffect(() => {
        checkAuth();
    }, []);

    const value = React.useMemo(() => ({
        user,
        loading,
        checked,

        // Методы авторизации
        login,
        logout,
        register,
        verifyEmailCode,
        updateUser,
        refreshUser,
        checkAuth,
        refreshToken,

        // Проверки состояния
        isAuthenticated: !!user,
        isAdmin,
        isStudent,
        isMentor,
        isTeacher,
        isOrganization,
        getUserRole,
        hasRole,
        hasPermission,
    }), [user, loading, checked]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
